// FEJLESZTÉS create/edit — jobb oldali beúszó `FormSheet`, KÉT módban (a support-teams/billing-minta).
//
// A form SZÁNDÉKOSAN nem tartalmaz publikálás-vezérlőt: a kihirdetés KÜLÖN jog (`product_updates.publish`) és
// külön művelet (a lista/részlet „Publikálás" akciója). Enélkül a `manage` jog egy legördülőn át kihirdethetne
// a partnereknek — és a külön jog jelentés nélküli lenne.
//
// KÉP a bejegyzésben: a LEÍRÁS szerkesztőjének kép-gombjával (a globális `editor-images` út) — külön
// fájl-feltöltés SZÁNDÉKOSAN nincs (user-döntés): egy per-tétel csatolmány-lista két helyre osztotta volna
// ugyanazt a képet, és a partner-oldalon két megjelenítési utat igényelt volna.
import type { ProductUpdateDetail } from '@atlas/contracts';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProductUpdateTypeBadge } from '@/components/product-updates/product-update-type-badge';
import { BilingualField } from '@/components/shared/bilingual-field';
import { DateField, RichTextField, SelectField, TextField } from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { productUpdateKeys } from '@/features/product-updates/api';
import {
  PRODUCT_UPDATE_STAGES,
  PRODUCT_UPDATE_TYPES,
  PRODUCT_UPDATE_HORIZONS,
} from '@/features/product-updates/enums';
import {
  buildProductUpdateResolver,
  EMPTY_PRODUCT_UPDATE_FORM,
  toProductUpdateBody,
  toProductUpdateForm,
  type ProductUpdateFormValues,
} from '@/features/product-updates/form';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { cn } from '@/lib/utils';

export interface ProductUpdateFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` → LÉTREHOZÁS; egyébként a szerkesztendő tétel (OCC `version` + előtöltés). */
  item: ProductUpdateDetail | null;
}

export function ProductUpdateFormSheet({
  open,
  onOpenChange,
  item,
}: ProductUpdateFormSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isEdit = item !== null;

  const form = useForm<ProductUpdateFormValues>({
    resolver: buildProductUpdateResolver(),
    defaultValues: EMPTY_PRODUCT_UPDATE_FORM,
    mode: 'onTouched',
  });
  const { reset, handleSubmit, watch, setValue } = form;

  // A sheet MEGNYITÁSAKOR töltünk (nem rendereléskor): a szerkesztő-példány ugyanaz a komponens, csak más sor.
  useEffect(() => {
    if (!open) return;
    reset(item === null ? EMPTY_PRODUCT_UPDATE_FORM : toProductUpdateForm(item));
  }, [open, item, reset]);

  const stage = watch('stage');
  const type = watch('type');

  const mutation = useEntityMutation<ProductUpdateFormValues, unknown>({
    mutationFn: (values) => {
      const body = toProductUpdateBody(values) as never;
      return isEdit
        ? api.productUpdates.update.mutation({
            params: { id: item.id },
            // OCC: a szerkesztés a BETÖLTÖTT sor verziójára hivatkozik — közben történt módosításnál 409.
            headers: { 'if-match': String(item.version) },
            body,
          })
        : api.productUpdates.create.mutation({ body });
    },
    invalidateKeys: [
      productUpdateKeys.lists(),
      ...(isEdit ? [productUpdateKeys.detail(item.id)] : []),
    ],
    messages: {
      success: isEdit ? t('toast:productUpdates.saved') : t('toast:productUpdates.created'),
      conflict: t('toast:productUpdates.conflict'),
      forbidden: t('toast:productUpdates.forbidden'),
      validation: t('toast:productUpdates.validation'),
      error: t('toast:productUpdates.error'),
    },
  });

  const onSubmit = handleSubmit((values) =>
    mutation.mutate(values, { onSuccess: () => onOpenChange(false) }),
  );

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY_PRODUCT_UPDATE_FORM);
        onOpenChange(o);
      }}
      title={isEdit ? t('productUpdates.editTitle') : t('productUpdates.createTitle')}
      description={isEdit ? item.title.hu : t('productUpdates.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('productUpdates.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('productUpdates.saving') : t('productUpdates.save')}
          </Button>
        </>
      }
    >
      <Form {...form}>
        <BilingualField<ProductUpdateFormValues>
          variant="input"
          label={t('productUpdates.fields.title')}
          nameHu="titleHu"
          nameEn="titleEn"
          required
        />

        {/* AZONOSÍTÓ (slug) — közvetlenül a cím alatt (user-döntés): a címből képződik, és ez jelenik meg a
            partner-linkben, tehát ott a helye, ahol a cím. */}
        <TextField<ProductUpdateFormValues>
          name="slug"
          label={t('productUpdates.fields.slug')}
          hint={t('productUpdates.fields.slugHint')}
          autoComplete="off"
        />

        <SelectField<ProductUpdateFormValues>
          name="stage"
          label={t('productUpdates.fields.stage')}
          required
          options={PRODUCT_UPDATE_STAGES.map((s) => ({
            value: s,
            label: t(`productUpdates.stage.${s}`),
          }))}
        />

        {/* TÍPUS — a jegy-nyitó mintája: badge-választó (a kiválasztott teljes erővel, a többi halványan),
            így a form és a lista UGYANAZT a nyelvet beszéli (azonos szín + ikon + kisbetűs felirat). */}
        <div className="space-y-1.5">
          <Label>
            {t('productUpdates.fields.type')}
            <span className="text-destructive"> *</span>
          </Label>
          <div
            role="radiogroup"
            aria-label={t('productUpdates.fields.type')}
            className="flex flex-wrap gap-2"
          >
            {PRODUCT_UPDATE_TYPES.map((value) => {
              const active = type === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setValue('type', value, { shouldValidate: true })}
                  className={cn(
                    'focus-visible:ring-accent-600 rounded-full transition-opacity focus:outline-none focus-visible:ring-2',
                    active ? 'opacity-100' : 'opacity-45 hover:opacity-80',
                  )}
                >
                  <ProductUpdateTypeBadge type={value} label={t(`productUpdates.type.${value}`)} />
                </button>
              );
            })}
          </div>
        </div>

        {/* A KIADÁS dátuma és a HORIZONT egymást kizáró világ: a kiadott tételnek dátuma van (az idővonal
            ez szerint csoportosít és rendez), a tervezettnek horizontja (Most/Következik/Később). A szerver mindkét irányban
            érvényesíti (422), a felület pedig csak a relevánsat kínálja. */}
        {stage === 'shipped' ? (
          <DateField<ProductUpdateFormValues>
            name="shippedAt"
            label={t('productUpdates.fields.shippedAt')}
            hint={t('productUpdates.fields.shippedAtHint')}
            required
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {/* A HORIZONT-értékek KISBETŰSEK a választóban is: enum-ÉRTÉK, nem mondat-kezdet — ugyanaz a
                design-szabály, ami a jelvényeket (`domain-badge`) és a board-kártya idő-sorát kisbetűsíti.
                A kisbetűsítés LOKÁL-TUDATOS (`toLocaleLowerCase`), nem CSS: a `SelectValue` a kiválasztott
                tétel szövegét tükrözi, tehát a trigger is így jelenik meg. */}
            <SelectField<ProductUpdateFormValues>
              name="horizon"
              label={t('productUpdates.fields.horizon')}
              placeholder={t('productUpdates.fields.horizonPlaceholder')}
              options={PRODUCT_UPDATE_HORIZONS.map((h) => ({
                value: h,
                label: t(`productUpdates.horizon.${h}`).toLocaleLowerCase(i18n.language),
              }))}
            />
            <TextField<ProductUpdateFormValues>
              name="targetPeriod"
              label={t('productUpdates.fields.targetPeriod')}
              hint={t('productUpdates.fields.targetPeriodHint')}
              autoComplete="off"
            />
          </div>
        )}

        <RichTextField<ProductUpdateFormValues>
          name="bodyHu"
          label={t('productUpdates.fields.bodyHu')}
          hint={t('productUpdates.fields.bodyHint')}
          // A kép-gomb `imageAccountId` NÉLKÜL is működik: a BFF a BELSŐ (MORGENS) ügyfél fájl-terét
          // használja — a fejlesztés-bejegyzésnek nincs cél-ügyfele, a tartalmat a cég írja.
          imageAccountId={null}
        />
        <RichTextField<ProductUpdateFormValues>
          name="bodyEn"
          label={t('productUpdates.fields.bodyEn')}
          hint={t('productUpdates.fields.bodyEnHint')}
          imageAccountId={null}
        />
      </Form>
    </FormSheet>
  );
}
